/**
 * Funzioni "data access" tipizzate: thin wrappers di `gql()` che expongono
 * un'API a livello di dominio (es. `getCliente(codice)` invece di passare
 * stringhe GraphQL direttamente nei componenti).
 *
 * Mirror del pattern mobile in `enermed-app/aws-utils/interfaceContratto.js`.
 * A differenza del mobile (try-catch silenzioso + ritorno `false`), qui
 * lasciamo propagare gli errori così TanStack Query li gestisce uniformemente.
 */

import { gql } from './client'
import {
  CURR_DAY,
  CURR_HOUR,
  CURR_MONTH,
  CURR_POWER,
  GET_BOLLETTE_CONTRATTO,
  GET_CLIENTE,
  GET_CONSUMO_ANNO,
  GET_CONSUMO_BIMESTRE,
  GET_DETTAGLIO_BOLLETTA,
  GET_LETTURE,
  GET_NEWS,
  GET_PROMOZIONI,
  LIST_MESSAGGI,
  PREV_MONTH,
} from './queries'
import {
  MARK_MESSAGGIO_AS_READ,
  SEND_AUTOLETTURA,
  SET_BOLLETTA_VISTA,
  UPDATE_CLIENTE,
  UPDATE_ETICHETTA_POD,
} from './mutations'
import type {
  Bolletta,
  ConsumoBucket,
  DettaglioBollettaResponse,
  DeviceResponse,
  GetClienteResult,
  Lettura,
  Messaggio,
  News,
  PowerNow,
  Promozione,
} from './types'

// =====================================================================
// QUERIES
// =====================================================================

export async function fetchCliente(codice_cliente: string): Promise<GetClienteResult> {
  const data = await gql<{ getCliente: GetClienteResult }>(GET_CLIENTE, { codice_cliente })
  return data.getCliente
}

/**
 * Risultato del fetch dettaglio:
 *  - `dettaglio` = oggetto con i periodi (se disponibile)
 *  - `unavailable: true` = il backend ha risposto "false" (la bolletta non
 *    ha dettaglio per periodo: caso non-errore, va mostrato come empty state)
 */
export type DettaglioBollettaFetchResult =
  | { dettaglio: DettaglioBollettaResponse; unavailable: false }
  | { dettaglio: null; unavailable: true }

export async function fetchDettaglioBolletta(
  codice_cliente: string,
  codice_contratto: string,
  invoice: string,
): Promise<DettaglioBollettaFetchResult> {
  const data = await gql<{ getDettaglioBolletta: unknown }>(GET_DETTAGLIO_BOLLETTA, {
    codice_cliente,
    codice_contratto,
    invoice,
  })

  const raw = data.getDettaglioBolletta

  // Backend ritorna "false" (stringa) o false (boolean) quando il dettaglio
  // non è disponibile per quella bolletta. Replicato dal mobile.
  if (!raw || raw === 'false' || raw === false) {
    return { dettaglio: null, unavailable: true }
  }

  let parsed: unknown = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch {
      return { dettaglio: null, unavailable: true }
    }
  }

  if (!parsed || typeof parsed !== 'object') {
    return { dettaglio: null, unavailable: true }
  }

  return {
    dettaglio: parsed as DettaglioBollettaResponse,
    unavailable: false,
  }
}

export async function fetchBolletteContratto(
  codice_cliente: string,
  codice_contratto: string,
): Promise<Bolletta[]> {
  const data = await gql<{ getBolletteContratto: Bolletta[] }>(GET_BOLLETTE_CONTRATTO, {
    codice_cliente,
    codice_contratto,
  })
  return data.getBolletteContratto ?? []
}

export async function fetchConsumoBimestre(
  codice_cliente: string,
  codice_contratto: string,
): Promise<ConsumoBucket[]> {
  const data = await gql<{ getConsumoContrattoBimestre: ConsumoBucket[] }>(
    GET_CONSUMO_BIMESTRE,
    { codice_cliente, codice_contratto },
  )
  return data.getConsumoContrattoBimestre ?? []
}

export async function fetchConsumoAnno(
  codice_cliente: string,
  codice_contratto: string,
): Promise<ConsumoBucket[]> {
  const data = await gql<{ getConsumoContrattoAnno: ConsumoBucket[] }>(GET_CONSUMO_ANNO, {
    codice_cliente,
    codice_contratto,
  })
  return data.getConsumoContrattoAnno ?? []
}

export async function fetchNews(): Promise<News[]> {
  const data = await gql<{ getNews: News[] }>(GET_NEWS)
  return data.getNews ?? []
}

export async function fetchPromozioni(): Promise<Promozione[]> {
  const data = await gql<{ getPromozioni: Promozione[] }>(GET_PROMOZIONI)
  return data.getPromozioni ?? []
}

export async function fetchMessaggi(codice_cliente: string): Promise<Messaggio[]> {
  // Nota: il backend nomina la query `ListMessaggi` con L maiuscola.
  const data = await gql<{ ListMessaggi: Messaggio[] }>(LIST_MESSAGGI, { codice_cliente })
  return data.ListMessaggi ?? []
}

// ============================================================
// REALTIME / TIMED CONSUMPTIONS (richiedono device IoT sul contratto)
// ============================================================

export async function fetchCurrPower(
  devices: string[],
  codice_contratto: string,
): Promise<PowerNow | null> {
  const data = await gql<{ currPower: PowerNow | null }>(CURR_POWER, {
    devices,
    codice_contratto,
  })
  return data.currPower ?? null
}

export async function fetchCurrHour(
  codice_contratto: string,
  from_date?: string,
): Promise<DeviceResponse | null> {
  const data = await gql<{ currHour: DeviceResponse | null }>(CURR_HOUR, {
    codice_contratto,
    from_date: from_date ?? null,
  })
  return data.currHour ?? null
}

export async function fetchCurrDay(
  codice_contratto: string,
): Promise<DeviceResponse | null> {
  const data = await gql<{ currDay: DeviceResponse | null }>(CURR_DAY, {
    codice_contratto,
  })
  return data.currDay ?? null
}

export async function fetchCurrMonth(
  codice_contratto: string,
): Promise<DeviceResponse | null> {
  const data = await gql<{ currMonth: DeviceResponse | null }>(CURR_MONTH, {
    codice_contratto,
  })
  return data.currMonth ?? null
}

export async function fetchPrevMonth(
  codice_contratto: string,
  from_date: string,
): Promise<DeviceResponse | null> {
  const data = await gql<{ prevMonth: DeviceResponse | null }>(PREV_MONTH, {
    codice_contratto,
    from_date,
  })
  return data.prevMonth ?? null
}

export async function fetchLetture(codice_contratto: string): Promise<Lettura[]> {
  const data = await gql<{ getLetture: Lettura[] }>(GET_LETTURE, { codice_contratto })
  return data.getLetture ?? []
}

// =====================================================================
// MUTATIONS
// =====================================================================

export async function markBollettaVista(
  codice_cliente: string,
  codice_contratto: string,
  invoice: string,
): Promise<void> {
  await gql<{ setBollettaVista: unknown }>(SET_BOLLETTA_VISTA, {
    codice_cliente,
    codice_contratto,
    invoice,
  })
}

export async function sendAutoLettura(input: {
  codice_cliente: string
  codice_contratto: string
  /** Data nel formato AWSDate, es. "2026-05-21". */
  data_misura: string
  /** Tutti e tre i campi sono obbligatori (backend GraphQL Float!). */
  F1: number
  F2: number
  F3: number
}): Promise<void> {
  await gql<{ sendAutoLettura: unknown }>(SEND_AUTOLETTURA, input)
}

export type ClienteUpdate = {
  email?: string
  phone?: string
  stripe_cus_id?: string
}

export async function updateCliente(
  codice_cliente: string,
  vals: ClienteUpdate,
): Promise<void> {
  await gql<{ updateCliente: unknown }>(UPDATE_CLIENTE, { codice_cliente, vals })
}

export async function updateEtichettaPod(
  codice_pod: string,
  etichetta: string | null,
): Promise<void> {
  await gql<{ updateEtichettaPod: unknown }>(UPDATE_ETICHETTA_POD, {
    codice_pod,
    etichetta,
  })
}

export async function markMessaggioRead(
  codice_cliente: string,
  datetime: string,
): Promise<void> {
  await gql<{ markMessaggioAsRead: unknown }>(MARK_MESSAGGIO_AS_READ, {
    codice_cliente,
    datetime,
  })
}
